import { FC, useMemo } from "react";
import { Autocomplete, Chip, TextField } from "@mui/material";

import countries from "../../../../data/countries.json";

// Special country groups
const countryGroups = [
  { name: "EU (27)", code: "__EU__", code3: "__EU__" },
  { name: "EEA (30)", code: "__EEA__", code3: "__EEA__" },
  { name: "EEA + Switzerland (31)", code: "__EEA+CHE__", code3: "__EEA+CHE__" },
];

type CountryMultiSelectProps = {
  label?: string;
  name?: string;
  value: string[];
  onChange: (value: string[]) => void;
  allowCountryGroups?: boolean;
  [x: string]: any;
};

/**
 * Multi-select of countries using alpha-3 codes. Values and callbacks use `string[]` of alpha-3 codes.
 */
export const CountryMultiSelect: FC<CountryMultiSelectProps> = ({
  label = "Countries",
  name = "countries",
  value,
  onChange,
  allowCountryGroups = false,
  ...rest
}) => {
  const allOptions = useMemo(() => {
    return allowCountryGroups ? [...countryGroups, ...countries] : countries;
  }, [allowCountryGroups]);

  const selectedOptions = useMemo(() => {
    const codes = value || [];
    return allOptions.filter((option) => codes.includes(option.code3)).sort(
      (a, b) => a.name.localeCompare(b.name)
    );
  }, [value, allOptions]);

  return (
    <Autocomplete
      multiple
      options={allOptions}
      value={selectedOptions}
      onChange={(_, newValue) => onChange(newValue.map((o) => o.code3))}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(o, v) => o.code3 === v.code3}
      filterSelectedOptions
      renderTags={(tagValue, getTagProps) =>
        [...tagValue]
          .sort((a, b) => a.name.localeCompare(b.name))
          .map((option, index) => (
            <Chip
              {...getTagProps({ index })}
              key={option.code3}
              label={option.name}
              size="small"
            />
          ))
      }
      renderInput={(params) => (
        <TextField {...params} label={label} variant="filled" name={name} />
      )}
      {...rest}
    />
  );
};
